/**
 * Admin LLM Costs Page
 *
 * Shows LiteLLM cost data:
 * - Date range filter (7d/30d)
 * - Summary: Total cost, breakdown by model
 * - Per-model cost table
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import { dedupeRequest } from '../../lib/request-cache';
import {
  ArrowLeft,
  DollarSign,
  Cpu,
  AlertTriangle,
} from 'lucide-react';

interface SpendLog {
  request_id: string;
  spend: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  startTime: string;
  model: string;
  user: string;
}

interface ModelBreakdown {
  model: string;
  totalSpend: number;
  totalRequests: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
}

type DateRange = '7d' | '30d';

export function AdminBillingPage() {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [logs, setLogs] = useState<SpendLog[]>([]);
  const fetchCountRef = useRef(0);

  useEffect(() => {
    const fetchId = ++fetchCountRef.current;
    let cancelled = false;

    const fetchData = async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      setNotConfigured(false);

      try {
        const now = new Date();
        const daysAgo = dateRange === '7d' ? 7 : 30;
        const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

        const params = new URLSearchParams({
          start_date: startDate.toISOString().split('T')[0],
          end_date: now.toISOString().split('T')[0],
        });

        const cacheKey = `admin-litellm-costs-${params.toString()}`;
        const result = await dedupeRequest(cacheKey, () =>
          apiRequest<{ data: SpendLog[] }>(`/api/admin/litellm/spend/logs?${params}`)
        );

        if (!cancelled && fetchId === fetchCountRef.current) {
          const data = (result as any).data || result;
          setLogs(Array.isArray(data) ? data : []);
        }
      } catch (err: any) {
        if (!cancelled && fetchId === fetchCountRef.current) {
          if (err?.message?.includes('503') || err?.message?.includes('NOT_CONFIGURED')) {
            setNotConfigured(true);
          } else {
            setError(err instanceof Error ? err.message : 'Failed to load LLM cost data');
          }
        }
      } finally {
        if (!cancelled && fetchId === fetchCountRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [dateRange]);

  const totalCost = useMemo(() => logs.reduce((sum, log) => sum + (log.spend || 0), 0), [logs]);

  const modelBreakdown = useMemo(() => {
    const map = new Map<string, ModelBreakdown>();

    logs.forEach((log) => {
      const existing = map.get(log.model);
      if (existing) {
        existing.totalSpend += log.spend || 0;
        existing.totalRequests += 1;
        existing.totalTokens += log.total_tokens || 0;
        existing.promptTokens += log.prompt_tokens || 0;
        existing.completionTokens += log.completion_tokens || 0;
      } else {
        map.set(log.model, {
          model: log.model,
          totalSpend: log.spend || 0,
          totalRequests: 1,
          totalTokens: log.total_tokens || 0,
          promptTokens: log.prompt_tokens || 0,
          completionTokens: log.completion_tokens || 0,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [logs]);

  const userBreakdown = useMemo(() => {
    const map = new Map<string, { user: string; totalSpend: number; totalRequests: number }>();

    logs.forEach((log) => {
      const userId = log.user || 'unknown';
      const existing = map.get(userId);
      if (existing) {
        existing.totalSpend += log.spend || 0;
        existing.totalRequests += 1;
      } else {
        map.set(userId, {
          user: userId,
          totalSpend: log.spend || 0,
          totalRequests: 1,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [logs]);

  const formatCost = (amount: number) => `$${amount.toFixed(4)}`;
  const formatNumber = (num: number) => num.toLocaleString();

  if (notConfigured) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">LLM Costs</h1>
                <p className="mt-1 text-sm text-gray-500">Track LLM costs via LiteLLM proxy</p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">LiteLLM Not Configured</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              Set <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">LITELLM_PROXY_URL</code> and{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">LITELLM_API_KEY</code> environment
              variables to enable LLM cost tracking.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">LLM Costs</h1>
              <p className="mt-1 text-sm text-gray-500">Track LLM costs via LiteLLM proxy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Filter */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
            <div className="flex gap-2">
              {(['7d', '30d'] as DateRange[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    dateRange === range
                      ? 'bg-mazo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range === '7d' ? 'Last 7 days' : 'Last 30 days'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-600" />
            <p className="mt-4 text-gray-500">Loading LLM cost data...</p>
          </div>
        ) : (
          <>
            {/* Total Cost Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total LLM Cost</p>
                  <p className="mt-2 text-4xl font-bold text-gray-900">{formatCost(totalCost)}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {formatNumber(logs.length)} requests across {modelBreakdown.length} model{modelBreakdown.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-green-100">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            {/* Model Breakdown */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Cost by Model</h2>
              </div>

              {modelBreakdown.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No usage data for this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Requests
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Prompt Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Completion Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {modelBreakdown.map((row) => (
                        <tr key={row.model} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {row.model}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCost(row.totalSpend)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(row.totalRequests)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(row.promptTokens)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(row.completionTokens)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-mazo-600 rounded-full"
                                  style={{
                                    width: `${totalCost > 0 ? (row.totalSpend / totalCost) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">
                                {totalCost > 0 ? ((row.totalSpend / totalCost) * 100).toFixed(1) : '0'}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Per-User Spend */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Cost by User</h2>
              </div>

              {userBreakdown.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-gray-500">No user data for this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Requests
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userBreakdown.map((row) => (
                        <tr key={row.user} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.user}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCost(row.totalSpend)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(row.totalRequests)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-purple-500 rounded-full"
                                  style={{
                                    width: `${totalCost > 0 ? (row.totalSpend / totalCost) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">
                                {totalCost > 0 ? ((row.totalSpend / totalCost) * 100).toFixed(1) : '0'}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminBillingPage;
